import { format } from 'date-fns';
import styled from 'styled-components';

import ChevronLeft from 'svg/ChevronLeft';

import useWeekNavigation from '../hooks/useWeekNavigation';

interface DateNavigationProps {
  weeks?: 1 | 2;
}

export default function DateNavigation({ weeks = 1 }: DateNavigationProps = {}) {
  const { startDate, endDate, goToNextWeek, goToPreviousWeek, goToToday, canGoNext, canGoPrevious } = useWeekNavigation(
    {
      weeks,
    }
  );

  const suffix = weeks === 2 ? '-two-week' : '-week';
  const dataCy = {
    previous: `nav-previous${suffix}`,
    next: `nav-next${suffix}`,
    range: weeks === 2 ? 'two-week-date-range' : 'date-range',
    today: weeks === 2 ? 'nav-today-two-week' : 'nav-today',
  };
  const nextLabel = weeks === 2 ? 'Next two weeks' : 'Next week';

  const formatDateRange = () => {
    const start = format(startDate, 'EEE, MMMM d, yyyy');
    const end = format(endDate, 'EEE, MMMM d, yyyy');
    return `${start} – ${end}`;
  };

  return (
    <Container>
      <ArrowGroup>
        <NavArrow onClick={goToPreviousWeek} disabled={!canGoPrevious} data-cy={dataCy.previous}>
          <ChevronLeft width={10} height={10} />
        </NavArrow>
        <NavArrow onClick={goToNextWeek} disabled={!canGoNext} aria-label={nextLabel} data-cy={dataCy.next}>
          <MirroredChevron width={10} height={10} />
        </NavArrow>
      </ArrowGroup>

      <DateRange data-cy={dataCy.range}>{formatDateRange()}</DateRange>

      <TodayLink onClick={goToToday} data-cy={dataCy.today}>
        Today
      </TodayLink>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ArrowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MirroredChevron = styled(ChevronLeft)`
  transform: scaleX(-1);
`;

const NavArrow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const DateRange = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: ${({ theme }) => theme.colors.black};
`;

const TodayLink = styled.button`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black25};
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.black05};
  }
`;
